import dayjs, { dateInputFieldFormat, formatDateString } from '~libs/dayjs';

import { Row } from '~components/table-view';

// From lib/servicePeriod, NOT from lib/derive that re-exports it: derive imports the Redux store on
// its first line, so reaching through it drags the whole store graph into anything that imports this
// module - and a module loaded early enough in the import order then initialises `store` before the
// slices it depends on exist, which surfaces as the opaque "`name` is a required option for
// createSlice". BudgetCopy importing this file is what first made that chain reachable.
import { OPEN_ENDED_LABEL } from '~support-at-home/lib/servicePeriod';
import { COPY_STATUS } from '~support-at-home/models';
import type { Section, SectionItem } from '~support-at-home/models';

/**
 * A date range, both ends optional. A missing end means the range has no end, which is a different
 * thing from an unknown one - a Support at Home service with no end date is available indefinitely.
 */
export interface DateRange {
  start_date: string | null;
  end_date: string | null;
}

/**
 * A budget line paired with the Support at Home service period it books against.
 *
 * The web sees these as entries returned by the filtered-services endpoint: after the personal care
 * split the same local service type appears once per period, each carrying its own category and its
 * own dates.
 */
interface ServiceEntry {
  id: number;
  name?: string;
  sah_service?: {
    id?: string;
    name?: string;
    start_date?: string | null;
    end_date?: string | null;
    category?: { id?: string };
  };
}

const toDate = (value?: string | null): string | null =>
  value ? dayjs(value).format(dateInputFieldFormat) : null;

/**
 * The filtered-services entry a budget line books against.
 *
 * Matched on the (service type, category) pair rather than the service type alone. A service type
 * can now sit on more than one period, and the two periods exist precisely because their
 * contribution categories differ - so the section's category is what says which one this line is.
 * Matching on the service type alone would return whichever entry happened to sort first, which
 * after the personal care split is a coin toss between two different contribution rates.
 *
 * The entry rather than the period, so a caller wanting both the period and the service's name
 * resolves the row once - see findServiceDetail.
 */
const findServiceEntry = (
  serviceTypeId: number | string | undefined,
  categoryId: string | undefined,
  filteredServices: ServiceEntry[] = [],
): ServiceEntry | null => {
  if (!serviceTypeId || !categoryId) return null;

  const match = filteredServices.find(
    (service) => String(service.id) === String(serviceTypeId) && service.sah_service?.category?.id === categoryId,
  );

  return match?.sah_service ? match : null;
};

const toPeriod = (entry: ServiceEntry): DateRange => ({
  start_date: toDate(entry.sah_service?.start_date),
  end_date: toDate(entry.sah_service?.end_date),
});

/** The Support at Home service period a budget line books against. */
export const findServicePeriod = (
  serviceTypeId: number | string | undefined,
  categoryId: string | undefined,
  filteredServices: ServiceEntry[] = [],
): DateRange | null => {
  const match = findServiceEntry(serviceTypeId, categoryId, filteredServices);
  return match ? toPeriod(match) : null;
};

/** The service's own name and period, for explaining a restriction on the row that carries it. */
export const findServiceDetail = (
  serviceTypeId: number | string | undefined,
  categoryId: string | undefined,
  filteredServices: ServiceEntry[] = [],
): { name?: string; period: DateRange } | null => {
  const match = findServiceEntry(serviceTypeId, categoryId, filteredServices);
  if (!match) return null;

  return { name: match.sah_service?.name, period: toPeriod(match) };
};

/**
 * Narrow a range to the part of it a period covers, or null where the two never meet.
 *
 * Null is a real answer and has to stay distinguishable from "unchanged": a line whose service is
 * not available on any day of the range cannot be clipped into something valid, and quietly
 * returning the original would hide that.
 */
export const clipToPeriod = (range: DateRange, period: DateRange | null): DateRange | null => {
  if (!period) return range;

  const start = latest(range.start_date, period.start_date);
  const end = earliest(range.end_date, period.end_date);

  if (start && end && dayjs(start).isAfter(dayjs(end))) return null;

  return { start_date: start, end_date: end };
};

const latest = (left: string | null, right: string | null): string | null => {
  if (!left) return right;
  if (!right) return left;
  return dayjs(left).isAfter(dayjs(right)) ? left : right;
};

const earliest = (left: string | null, right: string | null): string | null => {
  if (!left) return right;
  if (!right) return left;
  return dayjs(left).isBefore(dayjs(right)) ? left : right;
};

/** Is a range wholly inside a period? A range poking out at either end is not covered. */
export const isWithinPeriod = (range: DateRange, period: DateRange | null): boolean => {
  if (!period) return true;

  const clipped = clipToPeriod(range, period);
  if (!clipped) return false;

  return clipped.start_date === toDate(range.start_date) && clipped.end_date === toDate(range.end_date);
};

/**
 * The dates a newly added line should carry.
 *
 * The budget's own window, narrowed to the part of it the chosen service is available for. Picking
 * a service that ends part way through the budget lands a line dated to the part before it ends,
 * rather than one dated to the whole budget that will be rejected on save.
 */
export const deriveItemDates = (
  budgetRange: DateRange,
  period: DateRange | null,
): DateRange | null => clipToPeriod(budgetRange, period);

/**
 * Bring existing lines back inside the periods their services are available for.
 *
 * This is what the refresh control runs, and it is deliberately narrow. It only ever narrows dates
 * on lines that are already there: no line is added, none is removed, and nothing moves between
 * sections. A line whose service covers the whole of its current range is left untouched, so a
 * repeated refresh is a no-op.
 *
 * A line whose service is not available for any day of its range is left alone rather than blanked.
 * There is no correct date to give it, and it still has to fail validation on save so the provider
 * deals with it deliberately.
 */
export const refreshItemDatesToServicePeriods = (
  sections: Section[],
  filteredServices: ServiceEntry[] = [],
): Section[] =>
  sections.map((section) => ({
    ...section,
    items: (section.items ?? []).map((item) => {
      const period = findServicePeriod(item.service_type_id, section.category_id, filteredServices);
      if (!period) return item;

      const clipped = clipToPeriod({ start_date: item.start_date, end_date: item.end_date }, period);
      if (!clipped || !clipped.start_date || !clipped.end_date) return item;

      if (clipped.start_date === toDate(item.start_date) && clipped.end_date === toDate(item.end_date)) {
        return item;
      }

      return { ...item, start_date: clipped.start_date, end_date: clipped.end_date };
    }),
  }));

/** Has a line's date moved from what was loaded? Drives the changed-value highlight on refresh. */
export const hasItemDateChanged = (item: Row | SectionItem, initialRows: SectionItem[] = []): boolean => {
  const initial = initialRows.find(({ id }) => id === item.id);
  if (!initial) return false;

  return toDate(initial.start_date) !== toDate(item.start_date) || toDate(initial.end_date) !== toDate(item.end_date);
};

/** A period as one readable range, matching how price plan entries read it. */
export const formatRange = (range: DateRange | null): string => {
  if (!range?.start_date) return '';
  return `${formatDateString(range.start_date)} - ${
    range.end_date ? formatDateString(range.end_date) : OPEN_ENDED_LABEL
  }`;
};

/**
 * The lines in a budget that are dated somewhere they cannot be saved, each with the reason.
 *
 * The same two rules the server asserts on save, in the same order: inside the budget's own window,
 * and inside a period the line's service is available for under its section's category. Reading them
 * here is what lets the wizard stop a provider on the step that can still fix the line, rather than
 * letting them reach a read-only Review and a save error naming a line nothing on screen points at.
 *
 * Lines already dropped by a copy are skipped - they never reach the save, so flagging one would ask
 * a provider to fix something the application has already dealt with.
 */
export const findDateInvalidItems = (
  sections: Section[],
  budgetRange: DateRange,
  filteredServices: ServiceEntry[] = [],
): { item: SectionItem; reason: string }[] => {
  const invalid: { item: SectionItem; reason: string }[] = [];

  for (const section of sections ?? []) {
    for (const item of section.items ?? []) {
      if (!item?.service_type_id) continue;
      if (item.copy_status === COPY_STATUS.REMOVED) continue;
      if (!item.start_date || !item.end_date) continue;

      const range: DateRange = { start_date: item.start_date, end_date: item.end_date };
      const detail = findServiceDetail(item.service_type_id, section.category_id, filteredServices);
      const name = detail?.name ?? item.service_type?.name ?? 'A service';

      if (!isWithinPeriod(range, budgetRange)) {
        invalid.push({
          item,
          reason: `${name} is dated ${formatRange(range)}, outside the budget period ${formatRange(budgetRange)}.`,
        });
        continue;
      }

      if (detail && !isWithinPeriod(range, detail.period)) {
        invalid.push({
          item,
          reason: `${name} is dated ${formatRange(range)}, but is only available ${formatRange(detail.period)}.`,
        });
      }
    }
  }

  return invalid;
};
